import { DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Input, Select, Space, Table } from "antd";
import axios from "axios";
import moment from "moment";
import React, { useState } from "react";
import { useQuery } from "react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { serverUrl } from "../../../../../nestserver";

const AdminMontessoriHistoryCard = () => {
  const { Option } = Select;
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const classIdFromUrl = queryParams.get("classId") || null;
  const templateIdFromUrl = queryParams.get("templateId") || "";
  const termFromUrl = queryParams.get("term") || "Term 1";

  const [classId, setClassId] = useState(classIdFromUrl);
  const [templateId, setTemplateId] = useState(templateIdFromUrl);
  const [selectedTerm, setSelectedTerm] = useState(termFromUrl);
  const [downloadingId, setDownloadingId] = useState(null);

  const handleDownload = async (id, firstName, lastName) => {
    try {
      setDownloadingId(id);
      const res = await axios({
        method: "get",
        url: `${serverUrl}/montessori-progress-card/${id}/download`,
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Montessori_Report_${firstName}_${lastName}.pdf`,
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.log("Error downloading PDF:", error);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleClassChange = (value) => {
    setClassId(value);
    setTemplateId("");
    queryParams.set("classId", value);
    queryParams.delete("templateId");
    navigate({ search: queryParams.toString() });
  };

  const handleTemplateChange = (value) => {
    const val = value || "";
    setTemplateId(val);
    if (val) {
      queryParams.set("templateId", val);
    } else {
      queryParams.delete("templateId");
    }
    navigate({ search: queryParams.toString() });
  };

  const handleTermChange = (value) => {
    setSelectedTerm(value);
    queryParams.set("term", value);
    navigate({ search: queryParams.toString() });
  };

  // fetch ALL classes for admin
  async function fetchClasses() {
    const res = await axios({
      method: "get",
      url: serverUrl + "/classes/",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  }
  const classes = useQuery("classes", fetchClasses);

  // fetch templates for selected class
  async function fetchTemplates(classId) {
    const res = await axios({
      method: "get",
      url: `${serverUrl}/montessori-progress-card-template/?classId=${classId}`,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  }
  const getTemplate = useQuery(
    ["admin-montessori-progress-card-template", classId],
    () => fetchTemplates(classId),
    { enabled: !!classId },
  );

  async function fetchStudents(classId, templateId, term) {
    const res = await axios({
      method: "get",
      url: `${serverUrl}/montessori-progress-card/history/?class=${classId}&term=${term}&status=notified&status=acknowledged${
        templateId ? `&template=${templateId}` : ""
      }`,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  }
  const getStudents = useQuery(
    [
      "admin-montessori-progress-card-history",
      classId,
      templateId,
      selectedTerm,
    ],
    () => fetchStudents(classId, templateId, selectedTerm),
    { enabled: !!classId },
  );

  const filteredStudents = getStudents?.data?.result || [];

  // Search filter
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder="Search name"
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={
              <SearchOutlined
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              />
            }
            size="small"
            style={{
              width: 90,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record?.name
        ? record.name.toString().toLowerCase().includes(value.toLowerCase())
        : "",
    render: (text) => text,
  });

  const columns = [
    {
      title: "Student Id",
      dataIndex: "unique_id",
      key: "unique_id",
      align: "center",
      render: (text) => <div className="text-base text-center">{text}</div>,
    },
    {
      title: "Name",
      key: "name",
      align: "center",
      ...getColumnSearchProps("first_name"),
      render: (record) => (
        <div className="text-base font-semibold text-center capitalize">
          {record?.first_name} {record?.last_name}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "term_status",
      key: "status",
      align: "center",
      render: (text) => (
        <div className="text-base text-center capitalize">{text || "--"}</div>
      ),
    },
    {
      title: "Last Notified",
      dataIndex: "notified_at",
      key: "notified_at",
      align: "center",
      render: (text) => (
        <div className="text-base text-center capitalize">
          {text && text !== "--"
            ? moment(text).format("DD MMM , hh:mm A")
            : "--"}
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (record) => (
        <Space size="middle">
          <div
            className="text-base font-semibold text-center text-blue-500 cursor-pointer"
            onClick={() =>
              navigate(
                `/dashboard/admin/menu/montessori-template-history/${record?.progress_card?._id}?term=${selectedTerm}`,
              )
            }
          >
            View
          </div>
          <div
            className={`text-base font-semibold text-center text-orange-500 flex items-center gap-1 ${
              downloadingId === record?.progress_card?._id
                ? "opacity-50 pointer-events-none"
                : "cursor-pointer"
            }`}
            onClick={() =>
              handleDownload(
                record?.progress_card?._id,
                record?.first_name,
                record?.last_name,
              )
            }
          >
            <DownloadOutlined />{" "}
            {downloadingId === record?.progress_card?._id
              ? "Downloading..."
              : "Download"}
          </div>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="mt-8">
        <div
          style={{ borderRadius: "10px", color: "#616161" }}
          className="bg-white drop-shadow-md p-8 w-full"
        >
          <div className="text-2xl text-orange-500 font-semibold pb-8">
            Report History
          </div>
          <div className="flex gap-8 items-center mb-6 w-full">
            <div
              className="flex gap-4 items-center"
              style={{ width: "22%", minWidth: "160px" }}
            >
              <h3 className="text-base font-semibold shrink-0">Class</h3>
              <Select
                showSearch
                placeholder="Select Class"
                style={{ width: "100%" }}
                onChange={handleClassChange}
                value={classId}
              >
                {classes?.data?.map((data) => (
                  <Option key={data._id} value={data._id}>
                    {data.name}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="flex gap-4 items-center flex-1">
              <h3 className="text-base font-semibold shrink-0">Template</h3>
              <Select
                showSearch
                placeholder="Select template"
                style={{ width: "100%", minWidth: "150px" }}
                onChange={handleTemplateChange}
                value={templateId}
                allowClear
              >
                {getTemplate?.data?.result?.map((data) => (
                  <Option key={data._id} value={data._id}>
                    {data.name}
                  </Option>
                ))}
              </Select>
            </div>
            <div
              className="flex gap-4 items-center"
              style={{ width: "18%", minWidth: "130px" }}
            >
              <h3 className="text-base font-semibold shrink-0">Term</h3>
              <Select
                value={selectedTerm}
                onChange={handleTermChange}
                style={{ width: "100%" }}
              >
                <Option value="Term 1">Term 1</Option>
                <Option value="Term 2">Term 2</Option>
                <Option value="Term 3">Term 3</Option>
              </Select>
            </div>
          </div>
          <div className="py-5">
            <Table
              columns={columns}
              dataSource={filteredStudents}
              pagination={true}
              rowKey="_id"
              loading={getStudents?.isLoading}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminMontessoriHistoryCard;
